import React, { useContext, useEffect, useState } from 'react'
import { useQuery, gql } from '@apollo/client'
import { Grid, Box } from '@mui/material'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { STATES_WITH_ABBREVIATION } from '@mth/constants'
import { Paragraph } from '@mth/core/components/shared/Paragraph/Paragraph'
import { Title } from '@mth/core/components/shared/Title/Title'
import { MthColor, RoleLabels } from '@mth/enums'
import { ProfileContext } from '@mth/providers/ProfileProvider/ProfileContext'
import { UserContext } from '@mth/providers/UserContext/UserProvider'
import { getStudentGradeLevelBySY, parseGradeLevel, phoneFormat } from '@mth/utils'
import { Packet } from '../../../HomeroomStudentProfile/Student/types'

dayjs.extend(utc)
dayjs.extend(timezone)

const getSchoolYearsByRegionId = gql`
  query Region($regionId: ID!) {
    region(id: $regionId) {
      SchoolYears {
        school_year_id
        date_begin
        date_end
        grades
        birth_date_cut
        special_ed
        special_ed_options
        enrollment_packet
        midyear_application
        midyear_application_close
        midyear_application_open
      }
    }
  }
`

type EnrollmentJobsInfoProps = {
  packet: Packet
  handleModem: () => void
  refetch: () => void
}

export const EnrollmentJobsInfo: React.FC<EnrollmentJobsInfoProps> = ({ packet, handleModem, refetch }) => {
  const { showModal } = useContext(ProfileContext)
  const { me } = useContext(UserContext)
  const [specialEdOptions, setSpecialEdOptions] = useState<string[]>([])

  const { data: schoolYearData } = useQuery(getSchoolYearsByRegionId, {
    variables: {
      regionId: me?.selectedRegionId,
    },
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (schoolYearData?.region?.SchoolYears) {
      const shoolYears = schoolYearData?.region?.SchoolYears || []
      let special_ed_options = ''
      shoolYears
        // @ts-ignore
        .filter((item) => new Date(item.date_begin) <= new Date() && new Date(item.date_end) >= new Date())
        .map((item: { special_ed_options: string; special_ed: boolean }): void => {
          if (
            item.special_ed == true &&
            item.special_ed_options != '' &&
            item.special_ed_options != null &&
            special_ed_options == ''
          )
            special_ed_options = item.special_ed_options
        })
      if (special_ed_options == '') setSpecialEdOptions([])
      else setSpecialEdOptions(special_ed_options.split(','))
    }
  }, [schoolYearData?.region?.SchoolYears])

  const refreshModal = () => {
    refetch()
    handleModem()
  }

  // @ts-ignore
  const profileClicked = (profileData) => {
    handleModem()
    showModal(profileData, refreshModal)
  }

  const student = packet?.student

  const age = student?.person.date_of_birth ? dayjs().diff(student?.person.date_of_birth, 'years', false) : undefined

  const street = student?.parent.person.address.street
  const street2 = student?.parent.person.address.street2
  const gradeLevel = getStudentGradeLevelBySY(student, Number(packet.school_year_id))

  function studentSPED() {
    if (specialEdOptions.length > Number(student?.special_ed)) return specialEdOptions[Number(student?.special_ed)]
    return 'None'
  }

  const getState = (state: string | null) => {
    if (state) {
      return state + ','
    }
    // @ts-ignore
    const defaultState = student?.parent?.person?.user?.userRegions[0].regionDetail.name
    if (defaultState && STATES_WITH_ABBREVIATION[defaultState]) {
      return STATES_WITH_ABBREVIATION[defaultState] + ','
    }
    return ''
  }

  return (
    <Grid container columnSpacing={4}>
      {!packet ? (
        <Box sx={{ minHeight: '200px' }}></Box>
      ) : (
        <>
          <Grid
            sx={{
              '&.MuiGrid-root': {
                paddingRight: '3px',
              },
            }}
            item
            md={4}
            sm={12}
            xs={12}
            xl={6}
          >
            <Title
              sx={{
                color: MthColor.VORTEX,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              size='small'
              fontWeight='700'
            >
              <span onClick={() => profileClicked({ ...student, role: RoleLabels.STUDENT })}>
                {student?.person.first_name} {student?.person.last_name}
              </span>
            </Title>

            {/* @ts-ignore */}
            <Paragraph sx={{ marginY: '4px', fontSize: '14px' }} color={MthColor.SYSTEM_01} fontWeight='bold'>
              <b>
                {student?.person.preferred_first_name && student?.person.preferred_last_name
                  ? `${student?.person.preferred_first_name} ${student?.person.preferred_last_name}`
                  : 'Not found'}
              </b>
            </Paragraph>
            {/* @ts-ignore */}
            <Paragraph sx={{ marginY: '4px', fontSize: '14px' }} color={MthColor.SYSTEM_05} fontWeight='bold'>
              Gender:{' '}
              <b style={{ color: MthColor.SYSTEM_01 }}>
                {student?.person.gender ? student?.person.gender : 'Not found'}
              </b>
            </Paragraph>
            {/* @ts-ignore */}
            <Paragraph sx={{ fontSize: '14px' }} color={MthColor.SYSTEM_05} fontWeight='bold'>
              DOB:{' '}
              <b style={{ color: MthColor.SYSTEM_01 }}>
                {student?.person.date_of_birth
                  ? dayjs(student?.person.date_of_birth).tz('UTC').format('MMMM D, YYYY')
                  : 'Not found'}
                {age ? ` (${age})` : ''}
              </b>
            </Paragraph>
            {/* @ts-ignore */}
            <Paragraph sx={{ marginY: '4px', fontSize: '14px' }} color={MthColor.SYSTEM_01} fontWeight='bold'>
              {parseGradeLevel(gradeLevel)}
            </Paragraph>
            {/* @ts-ignore */}
            <Paragraph sx={{ marginY: '4px', fontSize: '14px' }} color={MthColor.SYSTEM_05} fontWeight='bold'>
              SPED: <span style={{ color: MthColor.SYSTEM_01 }}>{studentSPED()}</span>
            </Paragraph>
          </Grid>
          <Grid
            item
            md={6}
            sm={6}
            xs={12}
            sx={{
              '&.MuiGrid-root': {
                maxWidth: '12rem',
              },
            }}
          >
            <Title
              sx={{
                color: MthColor.VORTEX,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              size='small'
              fontWeight='700'
            >
              <span onClick={() => profileClicked({ ...student?.parent, role: RoleLabels.PARENT })}>
                {student?.parent.person.first_name} {student?.parent.person.last_name}
              </span>
            </Title>
            <Paragraph
              // @ts-ignore
              color={MthColor.LIGHTPURPLE}
              sx={{ fontSize: '14px', marginY: '4px', marginRight: '10px' }}
              fontWeight='400'
            >
              {student?.parent.person.email
                ? `${student?.parent.person.email}`
                : // @ts-ignore
                  student?.parent.person.user.email
                  ? student?.parent.person.user.email
                  : 'Not found'}
            </Paragraph>
            {/* @ts-ignore */}
            <Paragraph color={MthColor.SYSTEM_01} sx={{ fontSize: '14px', marginY: '4px' }} fontWeight='400'>
              {/* @ts-ignore */}
              {student?.parent.phone.number ? phoneFormat(student?.parent.phone.number) : 'Not found'}
            </Paragraph>
            {/* @ts-ignore */}
            <Paragraph color={MthColor.SYSTEM_01} sx={{ fontSize: '14px', marginY: '4px' }} fontWeight='400'>
              {street ? `${street} ${street2 ? ', ' + street2 : ''}` : 'Not found'}
            </Paragraph>
            {/* @ts-ignore */}
            <Paragraph color={MthColor.SYSTEM_01} sx={{ fontSize: '14px', marginY: '4px' }} fontWeight='400'>
              {!student?.parent.person.address.city &&
              !student?.parent.person.address.state &&
              !student?.parent.person.address.zip
                ? 'Not found'
                : `${student?.parent.person.address.city ? student?.parent.person.address.city + ',' : ''} ${getState(
                    student?.parent.person.address.state,
                  )}
                ${student?.parent.person.address.zip || ''}`}
            </Paragraph>
          </Grid>
        </>
      )}
    </Grid>
  )
}
